<script setup lang="ts">
import {computed, ref} from "vue";
import {ProfileResponseType} from "@/backend/profile.backend.ts";
import PersonChip from "@/components/user/PersonChip.vue";
import Button from "primevue/button";
import Listbox from "primevue/listbox";
import Popover from "primevue/popover";
import hOSAvatar from "@/components/hOSAvatar.vue";
import {profileBackend} from "@/backend/profile.backend";
import useAppToast from "@/composables/useAppToast";
import {backend} from "@/backend/backend";
import {useAuthStore} from "@/stores";

type Props = {
  profile: ProfileResponseType | undefined
}

const props = defineProps<Props>();
const emit = defineEmits<{
  'update:profile': [profile: ProfileResponseType];
}>();

const authStore = useAuthStore();
const toaster = useAppToast();

const user = computed(() => props.profile?.user);
const leaders = computed(() => user.value?.leaders || []);
const directReports = computed(() => user.value?.people || []);

const hasLeaders = computed(() => leaders.value.length > 0);
const hasDirectReports = computed(() => directReports.value.length > 0);

const allUsers = ref<any[]>([]);
const selectedLeaders = ref<any[]>([]);
const selectedDirectReports = ref<any[]>([]);
const addLeadersPopover = ref();
const addDirectReportsPopover = ref();

// Editing is always enabled for admins
const canEdit = computed(() => authStore.isAdmin);

const availableLeaders = computed(() => {
  if (!allUsers.value || !user.value) return [];
  const currentLeaderIds = leaders.value.map(l => l.id);
  return allUsers.value.filter(u => u.id !== user.value!.id && !currentLeaderIds.includes(u.id));
});

const availableDirectReports = computed(() => {
  if (!allUsers.value || !user.value) return [];
  const currentReportIds = directReports.value.map(r => r.id);
  return allUsers.value.filter(u => u.id !== user.value!.id && !currentReportIds.includes(u.id));
});

// Load all users when component mounts for admins
if (canEdit.value) {
  allUsers.value = await backend.users();
}

function showAddLeadersPopover(event: Event) {
  addLeadersPopover.value.show(event);
}

function showAddDirectReportsPopover(event: Event) {
  addDirectReportsPopover.value.show(event);
}

async function addLeaders() {
  if (!user.value || selectedLeaders.value.length === 0) return;
  
  try {
    const userIds = selectedLeaders.value.map(u => u.id);
    const updatedProfile = await profileBackend.addLeaders(user.value.id, userIds);
    emit('update:profile', updatedProfile);
    selectedLeaders.value = [];
    addLeadersPopover.value.hide();
    toaster.success("Leaders added successfully");
  } catch (e) {
    toaster.error("Failed to add leaders");
    console.error(e);
  }
}

async function addDirectReports() {
  if (!user.value || selectedDirectReports.value.length === 0) return;
  
  try {
    const userIds = selectedDirectReports.value.map(u => u.id);
    const updatedProfile = await profileBackend.addDirectReports(user.value.id, userIds);
    emit('update:profile', updatedProfile);
    selectedDirectReports.value = [];
    addDirectReportsPopover.value.hide();
    toaster.success("Direct reports added successfully");
  } catch (e) {
    toaster.error("Failed to add direct reports");
    console.error(e);
  }
}

async function removeLeader(leaderId: number) {
  if (!user.value) return;
  
  try {
    const updatedProfile = await profileBackend.removeLeaders(user.value.id, [leaderId]);
    emit('update:profile', updatedProfile);
    toaster.success("Leader removed successfully");
  } catch (e) {
    toaster.error("Failed to remove leader");
    console.error(e);
  }
}

async function removeDirectReport(reportId: number) {
  if (!user.value) return;
  
  try {
    const updatedProfile = await profileBackend.removeDirectReports(user.value.id, [reportId]);
    emit('update:profile', updatedProfile);
    toaster.success("Direct report removed successfully");
  } catch (e) {
    toaster.error("Failed to remove direct report");
    console.error(e);
  }
}

const navigateToProfile = (username: string) => {
  window.location.href = `/people/${username}`;
};

async function setDirectManager(managerId: number) {
  if (!user.value) return;
  
  try {
    const updatedProfile = await profileBackend.setDirectManager(user.value.id, managerId);
    emit('update:profile', updatedProfile);
  } catch (e) {
    toaster.error("Failed to set direct manager");
    console.error(e);
  }
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Leaders Section -->
    <div v-if="hasLeaders || canEdit">
      <div class="flex justify-start items-center mb-4 gap-4">
        <h3 class="text-2xl font-semibold">Reports To</h3>
        <Button 
          v-if="canEdit"
          @click="showAddLeadersPopover"
          icon="pi pi-user-plus" 
          severity="secondary"
          rounded
          outlined
          v-tooltip.top="'Add leader'"
        />
      </div>
      
      <Popover ref="addLeadersPopover" @hide="selectedLeaders = []">
        <Listbox 
          v-model="selectedLeaders"
          :options="availableLeaders"
          optionLabel="name"
          placeholder="Select leaders"
          list-style="max-height: 30rem"
          multiple 
          filter
        >
          <template #option="slotProps">
            <div class="flex items-center gap-2">
              <hOSAvatar :user="slotProps.option" avatarClass="!w-8 !h-8"/>
              <div class="flex flex-col">
                <span>{{ slotProps.option.name }}</span>
                <span class="text-xs text-muted-color">{{ slotProps.option.title }}</span>
              </div>
            </div>
          </template>
        </Listbox>
        <div class="flex justify-end pt-3">
          <Button label="Add" @click="addLeaders" :disabled="selectedLeaders.length === 0"/>
        </div>
      </Popover>
      
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <PersonChip 
          v-for="leader in leaders" 
          :key="leader.id"
          :person="leader"
          :clickable="!canEdit"
          :isDirectManager="leader.id === user?.managerId"
          @click="navigateToProfile"
        >
          <template #actions v-if="canEdit">
            <Button 
              v-if="leader.id !== user?.managerId"
              @click.stop="setDirectManager(leader.id)"
              icon="pi pi-star"
              severity="secondary"
              rounded
              text
              size="small"
              v-tooltip.top="'Set as Direct Manager'"
              class="opacity-0 group-hover:opacity-100 transition-opacity"
            />
            <Button 
              @click.stop="removeLeader(leader.id)"
              icon="pi pi-times"
              severity="danger"
              rounded
              text
              size="small"
              v-tooltip.top="'Remove'"
            />
          </template>
        </PersonChip>
      </div>
      
      <div v-if="!hasLeaders && canEdit" class="text-center text-muted-color py-4">
        <p>No leaders assigned. Click the + button to add leaders.</p>
      </div>
    </div>

    <!-- Direct Reports Section -->
    <div v-if="hasDirectReports || canEdit">
      <div class="flex justify-start items-center mb-4 gap-4">
        <h3 class="text-2xl font-semibold">Direct Reports</h3>
        <Button 
          v-if="canEdit"
          @click="showAddDirectReportsPopover"
          icon="pi pi-user-plus" 
          severity="secondary"
          rounded
          outlined
          v-tooltip.top="'Add direct report'"
        />
      </div>
      
      <Popover ref="addDirectReportsPopover" @hide="selectedDirectReports = []">
        <Listbox 
          v-model="selectedDirectReports"
          :options="availableDirectReports"
          optionLabel="name"
          placeholder="Select direct reports"
          list-style="max-height: 30rem"
          multiple 
          filter
        >
          <template #option="slotProps">
            <div class="flex items-center gap-2">
              <hOSAvatar :user="slotProps.option" avatarClass="!w-8 !h-8"/>
              <div class="flex flex-col">
                <span>{{ slotProps.option.name }}</span>
                <span class="text-xs text-muted-color">{{ slotProps.option.title }}</span>
              </div>
            </div>
          </template>
        </Listbox>
        <div class="flex justify-end pt-3">
          <Button label="Add" @click="addDirectReports" :disabled="selectedDirectReports.length === 0"/>
        </div>
      </Popover>
      
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        <PersonChip 
          v-for="report in directReports" 
          :key="report.id"
          :person="report"
          :clickable="!canEdit"
          @click="navigateToProfile"
        >
          <template #actions v-if="canEdit">
            <Button 
              @click.stop="removeDirectReport(report.id)"
              icon="pi pi-times"
              severity="danger"
              rounded
              text
              size="small"
              v-tooltip.top="'Remove'"
            />
          </template>
        </PersonChip>
      </div>
      
      <div v-if="!hasDirectReports && canEdit" class="text-center text-muted-color py-4">
        <p>No direct reports assigned. Click the + button to add direct reports.</p>
      </div>
    </div>

    <!-- No data message -->
    <div v-if="!hasLeaders && !hasDirectReports && !canEdit" class="text-center text-muted-color py-8">
      <p class="text-lg">No organizational relationships found.</p>
    </div>
  </div>
</template>
